import { useEffect, useState } from "react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Download, FileText, Wallet, Landmark, TrendingDown, PlayCircle, Receipt, Mail, Send } from "lucide-react";

function rupee(x) { return "₹ " + Number(x || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

function monthLabel(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

function download(url, filename) {
  const token = localStorage.getItem("access_token");
  fetch(url, { headers: { Authorization: `Bearer ${token}` }, credentials: "include" })
    .then(r => { if (!r.ok) throw new Error("Download failed"); return r.blob(); })
    .then(blob => {
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    })
    .catch(e => toast.error(e.message));
}

export default function Payroll() {
  const { user } = useAuth();
  const isReviewer = user?.role === "admin" || user?.role === "hr";
  const [payslips, setPayslips] = useState([]);
  const [structure, setStructure] = useState(null);
  const [busy, setBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const load = async () => {
    const [ps, str, cfg] = await Promise.all([
      api.get("/payroll/payslips"),
      user?.employee_id ? api.get("/payroll/structure") : Promise.resolve({ data: null }),
      isReviewer ? api.get("/payroll/email/config").catch(() => ({ data: null })) : Promise.resolve({ data: null }),
    ]);
    setPayslips(ps.data);
    setStructure(str.data);
    setEmailConfig(cfg.data);
  };
  useEffect(() => { load(); }, []);

  const runPayroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/process?month=${month}`);
      toast.success(`Generated ${data.created} payslips for ${monthLabel(month)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const emailOne = async (p) => {
    setEmailBusy(p.id);
    try {
      const { data } = await api.post(`/payroll/payslip/${p.id}/email`);
      toast.success(`Payslip emailed to ${data.sent_to}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setEmailBusy(null); }
  };

  const emailBulk = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/email-bulk?month=${month}`);
      if (data.failed?.length) {
        toast.warning(`Sent ${data.sent}/${data.total}. Failed: ${data.failed.length}`);
      } else {
        toast.success(`Emailed ${data.sent}/${data.total} payslips`);
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="payroll-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase font-semibold tracking-[0.1em] text-slate-500">Payroll</div>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-slate-900">Salary, sorted.</h1>
          <p className="mt-1 text-sm text-slate-500">Structure, monthly payslips and Form 16 — all in one place.</p>
        </div>
        {isReviewer && (
          <div className="flex items-end gap-2">
            <div className="flex flex-col gap-1">
              <Label className="text-xs">Payroll month</Label>
              <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-40 h-10" data-testid="payroll-month" />
            </div>
            <Button disabled={busy} onClick={runPayroll} className="bg-[#DC2626] hover:bg-[#B91C1C] h-10" data-testid="run-payroll-btn">
              <PlayCircle className="h-4 w-4 mr-1.5" strokeWidth={1.8} />
              Run payroll
            </Button>
            <Button variant="outline" className="h-10" onClick={() => download(`${API}/reports/payroll.xlsx?month=${month}`, `payroll-${month}.xlsx`)} data-testid="payroll-xlsx-btn">
              <Download className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Excel
            </Button>
            <Button variant="outline" className="h-10 border-red-200 text-[#DC2626]" onClick={emailBulk} disabled={busy || !emailConfig?.configured} data-testid="email-bulk-btn" title={emailConfig?.configured ? `Send via ${emailConfig.provider} · from ${emailConfig.sender}` : "Configure O365 credentials first"}>
              <Send className="h-4 w-4 mr-1.5" strokeWidth={1.8} /> Email all
            </Button>
          </div>
        )}
      </div>

      {isReviewer && emailConfig && (
        <div className="text-xs text-slate-500 flex items-center gap-2" data-testid="email-config-line">
          <Mail className="h-3.5 w-3.5" strokeWidth={1.5} />
          {emailConfig.configured
            ? <>Emails go out from <span className="font-mono font-semibold text-slate-700">{emailConfig.sender}</span> via <span className="font-semibold">{emailConfig.provider}</span></>
            : <span className="text-orange-600">Payslip email not configured — set AZURE_TENANT_ID / CLIENT_ID / CLIENT_SECRET in backend/.env</span>}
        </div>
      )}

      {structure && (
        <Card className="p-6 rounded-xl border-slate-200/60">
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="h-4 w-4 text-[#DC2626]" strokeWidth={1.8} />
            <h3 className="font-display text-lg font-semibold text-slate-900">Your salary structure</h3>
            <Badge className="bg-red-50 text-[#DC2626] border-0 ml-2">Annual CTC {rupee(structure.ctc_annual)}</Badge>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <SBox label="Basic" value={structure.basic} />
            <SBox label="HRA" value={structure.hra} />
            <SBox label="Special Allow." value={structure.special_allowance} />
            <SBox label="Transport (annual)" value={structure.transport * 12} />
            <SBox label="Medical (annual)" value={structure.medical * 12} />
            <SBox label="PF (Employee)" value={structure.pf_employee} tone="red" />
            <SBox label="Prof. Tax (annual)" value={structure.professional_tax * 12} tone="red" />
            <SBox label="TDS (annual)" value={structure.tds} tone="red" />
          </div>
        </Card>
      )}

      <Tabs defaultValue="payslips">
        <TabsList>
          <TabsTrigger value="payslips" data-testid="tab-payslips">Payslips</TabsTrigger>
          {user?.employee_id && <TabsTrigger value="form16" data-testid="tab-form16">Form 16</TabsTrigger>}
        </TabsList>
        <TabsContent value="payslips">
          <div className="space-y-3">
            {payslips.length === 0 && <div className="text-sm text-slate-500 py-10 text-center border border-dashed rounded-xl">No payslips yet.</div>}
            {payslips.map(p => (
              <Card key={p.id} className="p-4 rounded-xl border-slate-200/60 flex flex-wrap items-center justify-between gap-3" data-testid={`payslip-${p.id}`}>
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-lg bg-red-50 text-[#DC2626] grid place-items-center">
                    <Receipt className="h-5 w-5" strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-slate-900">
                      {isReviewer ? `${p.employee_name} · ` : ""}{monthLabel(p.month)}
                    </div>
                    <div className="text-xs text-slate-500">{p.designation} · {p.department} · {p.employee_code}</div>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Gross</div>
                    <div className="text-sm font-semibold text-slate-900">{rupee(p.gross)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">Net</div>
                    <div className="text-sm font-bold text-emerald-600">{rupee(p.net_pay)}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => download(`${API}/payroll/payslip/${p.id}/pdf`, `payslip-${p.employee_code}-${p.month}.pdf`)} data-testid={`download-payslip-${p.id}`}>
                    <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> PDF
                  </Button>
                  {isReviewer && (
                    <Button size="sm" variant="outline" className="border-red-200 text-[#DC2626]" onClick={() => emailOne(p)} disabled={emailBusy === p.id || !emailConfig?.configured} data-testid={`email-payslip-${p.id}`}>
                      <Send className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> {emailBusy === p.id ? "Sending…" : "Email"}
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        </TabsContent>
        {user?.employee_id && (
          <TabsContent value="form16">
            <Card className="p-6 rounded-xl border-slate-200/60">
              <div className="flex items-center gap-3 mb-4">
                <FileText className="h-5 w-5 text-[#DC2626]" strokeWidth={1.8} />
                <h3 className="font-display text-lg font-semibold text-slate-900">Form 16 — annual tax certificate</h3>
              </div>
              <div className="space-y-3">
                {[2025, 2024, 2023].map(y => (
                  <div key={y} className="flex items-center justify-between border-b border-slate-100 py-3 last:border-0">
                    <div>
                      <div className="font-semibold text-sm text-slate-900">FY {y}-{y + 1}</div>
                      <div className="text-xs text-slate-500">Certificate under section 203 of the Income-tax Act, 1961</div>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => download(`${API}/payroll/form16/${user.employee_id}?year=${y}`, `form16-${y}.pdf`)} data-testid={`form16-${y}`}>
                      <Download className="h-3.5 w-3.5 mr-1.5" strokeWidth={1.8} /> Download
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function SBox({ label, value, tone = "blue" }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="text-[10px] uppercase font-semibold tracking-wider text-slate-500">{label}</div>
      <div className={`mt-1 text-lg font-bold ${tone === "red" ? "text-red-600" : "text-slate-900"}`}>{rupee(value)}</div>
    </div>
  );
}
